import React, { useState } from "react";
import { Select, Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { fetchSpecSections } from "../services/specSectionsAPI";
import { filterOption } from "../utils/textUtils";
import { useAuth } from "../hooks/useAuth";
import CreateSpecSectionModal from "./CreateSpecSectionModal";

const renderCreateSpecSectionFooter = (menu, onCreateClick) => (
  <>
    {menu}
    <div
      style={{
        borderTop: "1px solid #f0f0f0",
        padding: "8px",
      }}
    >
      <Button
        type="link"
        icon={<PlusOutlined />}
        onMouseDown={(event) => event.preventDefault()}
        onClick={onCreateClick}
        style={{ padding: 0, width: "100%", textAlign: "left" }}
      >
        Create Spec Section
      </Button>
    </div>
  </>
);

const formatSpecSectionLabel = (spec, separator = " ") =>
  `${spec.section_number}${separator}${spec.title}`;

const SpecSectionSelect = ({
  value,
  onChange,
  placeholder = "Select specification section",
  disabled = false,
  className,
  allowClear = true,
  allowCreate = true,
  labelSeparator = " ",
  ...rest
}) => {
  const { selectedProject } = useAuth();
  const projectId = selectedProject?.project_id;
  const userRole = selectedProject?.role;
  const canCreate =
    allowCreate &&
    (userRole === "owner" || userRole === "project_manager");
  const [showCreateModal, setShowCreateModal] = useState(false);

  const { data: specSections, refetch } = useQuery({
    queryKey: ["specSections", projectId],
    queryFn: () => fetchSpecSections(projectId),
    enabled: !!projectId,
  });

  const handleSpecSectionCreated = async (created) => {
    const result = await refetch();
    const createdId = String(created?.id || created?._id || "");
    const matched =
      result.data?.specSections?.find(
        (spec) => String(spec.id || spec._id) === createdId,
      ) || created;

    if (!matched || !onChange) {
      return;
    }

    onChange(matched.id || matched._id);
  };

  const dropdownRender = canCreate
    ? (menu) =>
        renderCreateSpecSectionFooter(menu, () => setShowCreateModal(true))
    : undefined;

  return (
    <>
      <Select
        showSearch
        optionFilterProp="children"
        filterOption={filterOption}
        value={value}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder}
        className={className}
        allowClear={allowClear}
        getPopupContainer={() => document.body}
        options={specSections?.specSections?.map((spec) => ({
          value: spec.id || spec._id,
          label: formatSpecSectionLabel(spec, labelSeparator),
        }))}
        dropdownRender={dropdownRender}
        {...rest}
      />
      <CreateSpecSectionModal
        visible={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSpecSectionCreated={handleSpecSectionCreated}
      />
    </>
  );
};

export default SpecSectionSelect;
